import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useInterval } from 'react-use';

import { ACTIONS } from '@/constants/actions';
import { useAvailableActions } from '@/hooks/useAvailableActions';
import { useUpdateAuditCaseFile } from '@/queries/update-audit-case-file';
import { useAuditStore } from '@/store/auditStore';
import { useNotificationStore } from '@/store/notificationStore';
import { createAuditFileBtoa } from '@/utils/createAuditFile';
import { checkUnsavedChanges } from '@/utils/unsavedChanges';

import { CloseAuditAction } from './close-audit-action';
import { DeleteAuditAction } from './delete-audit-action';
import { SaveAuditAction } from './save-audit-action';
import { SubmitAuditAction } from './submit-audit-action/submit-audit-action';
import styles from './tabs-actions.module.scss';

const AUTO_SAVE_INTERVAL = 2 * 60 * 1000; // 2 minutes

export const TabsActions = () => {
  const { t } = useTranslation();

  const audit = useAuditStore((state) => state.audit)!;
  const lastSavedAuditBtoa = useAuditStore((state) => state.lastSavedAuditBtoa);
  const setLastSavedAuditBtoa = useAuditStore((state) => state.setLastSavedAuditBtoa);
  const { availableActions } = useAvailableActions();

  const { audit_case_uuid: auditCaseUuid } = useParams();

  const { mutate: updateAuditCaseFile, isPending: isSaving } = useUpdateAuditCaseFile();
  const notify = useNotificationStore((state) => state.notify);

  if (!auditCaseUuid) {
    throw new Error('Audit case UUID is required');
  }

  useInterval(() => {
    if (availableActions.has(ACTIONS.AUDIT_CASE_UPDATE)) {
      handleSaveAudit();
    }
  }, AUTO_SAVE_INTERVAL);

  const handleSaveAudit = async (successCallback?: () => void) => {
    const saveAuditBtoa = createAuditFileBtoa(audit);

    updateAuditCaseFile(
      {
        auditCaseUuid,
        inputFile: saveAuditBtoa,
      },
      {
        onSuccess: () => {
          setLastSavedAuditBtoa(saveAuditBtoa);
          successCallback?.();
        },
      },
    );
  };

  const handleManualSaveAudit = (successCallback?: () => void) => {
    handleSaveAudit(() => {
      notify({
        message: t('common.success'),
        type: 'success',
      });
      successCallback?.();
    });
  };

  useEffect(() => {
    const closeEvent = (event: BeforeUnloadEvent) => {
      const hasUnsavedChanges = checkUnsavedChanges(lastSavedAuditBtoa, audit);

      if (hasUnsavedChanges) {
        event.preventDefault();
      }
    };

    window.addEventListener('beforeunload', closeEvent);

    return () => {
      window.removeEventListener('beforeunload', closeEvent);
    };
  }, [lastSavedAuditBtoa, audit]);

  return (
    <div className={styles.container}>
      <div className={styles.actions}>
        {availableActions.has(ACTIONS.AUDIT_CASE_DELETE) && (
          <DeleteAuditAction auditCaseUuid={auditCaseUuid} />
        )}
        {availableActions.has(ACTIONS.AUDIT_CASE_UPDATE) && (
          <SaveAuditAction isSaving={isSaving} onSaveAudit={handleManualSaveAudit} />
        )}
        <CloseAuditAction auditCaseUuid={auditCaseUuid} onSaveAudit={handleManualSaveAudit} />
      </div>
      {availableActions.has(ACTIONS.AUDIT_CASE_SUBMIT) && (
        <SubmitAuditAction auditCaseUuid={auditCaseUuid} />
      )}
    </div>
  );
};
